import { ApplicationCodes, CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import type { OfferType, GenericObject, PenciledInputOfferType } from '@makemydeal/dr-platform-types';
import {
    AppliedIncentiveIdsNode,
    AppliedIncentivesNode,
    ManualIncentivesNode,
    SelectedIncentivesNode
} from '../../../constants/penciledInputs';
import * as selectors from '../../../selectors';
import * as types from '../../../types/actionTypes';
import type { PartialPenciledInputsWithAllTypes, PenciledInputsState } from '../../../types/commonTypes';
import { isImplausibleMoneyFactor } from '../../../utils/rateUtils';
import {
    isDealerCash,
    isFrozenProgramRow,
    isNotDealerCash,
    isNotStoredDealerCash,
    isStoredDealerCash,
    isUserCustomIncentive,
    resolveIncentiveKey
} from '../../../utils/incentives';
import {
    aggregatedSelectedIncentives,
    applyPenciledInputsProgram,
    keepUserManualIncentives,
    updateInputWithValue,
    updateOfferTypeWithInputs
} from '../helpers';

const resolveSelectedIncentivesAfterManualChange = (
    state: PenciledInputsState,
    offerType: OfferType,
    restorationEnabled: boolean
): string[] => (restorationEnabled ? state.byOfferType?.[offerType]?.[SelectedIncentivesNode] ?? [] : []);

const programRatesAreImplausible = (programData: GenericObject): boolean =>
    isImplausibleMoneyFactor(programData.rateType, programData.sellRate, programData.buyRate);

const applyLeaseProgramRates = (node: GenericObject, programData: GenericObject, isManagerView: boolean): void => {
    node.rateType = programData.rateType;
    if (isManagerView) node.rateTypeOverride = programData.rateType;

    node.sellRateOverride = programData.sellRate;
    node.buyRateOverride = isManagerView ? programData.buyRate : undefined;
    node.rateOverride = programData.buyRate;
    node.overridesByTerm = {};
};

const applyFinanceProgramRates = (node: GenericObject, programData: GenericObject, isManagerView: boolean): void => {
    node.sellRateOverride = isManagerView ? programData.sellRate : undefined;
    node.buyRateOverride = isManagerView ? programData.buyRate : undefined;
    if (isManagerView) node.rateTypeOverride = programData.rateType;
    node.overridesByTerm = {};
};

const isSameManualIncentive = (a: any, b: any): boolean => {
    if (a._editId && b._editId) {
        return a._editId === b._editId;
    }
    if (isUserCustomIncentive(a) || isUserCustomIncentive(b)) {
        return false;
    }
    const keyA = resolveIncentiveKey(a);
    const keyB = resolveIncentiveKey(b);
    if (keyA != null && keyB != null) {
        return keyA === keyB;
    }
    return Boolean(a.name) && a.name === b.name && a.type === b.type;
};

const dedupeManualIncentives = (incentives: any[]): any[] =>
    incentives.reduce((kept: any[], incentive: any) => {
        const index = kept.findIndex((item) => isSameManualIncentive(item, incentive));
        if (index === -1) {
            kept.push(incentive);
        } else if (incentive.manual || !kept[index].manual) {
            kept[index] = incentive;
        }
        return kept;
    }, []);

const isForwardManualIncentive = (incentive: any): boolean => incentive.manual !== false;

/**
 * A frozen row is the deal's own record of what the operator applied, so it has to survive the edit paths
 * that rewrite the bag for unrelated reasons - the slideout's cancel-revert and any manual add/edit/remove.
 *
 * Deliberately NOT applied to `keepUserManualIncentives` - see the note there.
 */
const shouldRetainManualRow =
    (keepFrozenProgramRows: boolean) =>
    (incentive: any): boolean =>
        isForwardManualIncentive(incentive) || (keepFrozenProgramRows && isFrozenProgramRow(incentive));

const sumAmounts = (incentives: any[], predicate: (incentive: any) => boolean): number =>
    incentives.filter(predicate).reduce((sum, incentive) => sum + (incentive.amount || 0), 0);

const sumDealerCash = (incentives: any[], restorationEnabled: boolean): number =>
    sumAmounts(incentives, restorationEnabled ? isStoredDealerCash : isDealerCash);

const sumNonDealerCash = (incentives: any[], restorationEnabled: boolean): number =>
    sumAmounts(incentives, restorationEnabled ? isNotStoredDealerCash : isNotDealerCash);

const withForwardManualIncentivesOnly = (manualIncentives: any, keepFrozenProgramRows: boolean): any => {
    const incentives = (manualIncentives.incentives || []).filter(shouldRetainManualRow(keepFrozenProgramRows));
    return {
        ...manualIncentives,
        incentives,
        dealerCash: sumDealerCash(incentives, true),
        totalRebates: sumNonDealerCash(incentives, true)
    };
};

const isNonConditionalSelectionKey = (key: unknown): key is string => typeof key === 'string' && key.startsWith('nonconditional_');

/**
 * APPLIED_PROGRAM writes `selectedIncentives` but historically left `appliedIncentiveIds` and the
 * `appliedIncentives` breakdown untouched, so a standard incentive unchecked with a program applied
 * stayed in both. `seedSelectedTermAppliedIncentivesFromPenciled` then re-seeded the selected term
 * from those stale nodes, overwriting a correct payment response.
 */
const withAppliedSelectionNodes = (entry: GenericObject, selectionKeys: string[], previousEntry?: GenericObject): void => {
    const nonConditionalKeys = selectionKeys.filter(isNonConditionalSelectionKey);
    const conditionalKeys = selectionKeys.filter((key) => !isNonConditionalSelectionKey(key));
    // An empty non-conditional list means the payload deferred standards to the service rather than
    // asserting "none" (which arrives as the `_none` sentinel), so the recorded set carries forward.
    const resolvedNonConditionalKeys =
        nonConditionalKeys.length > 0
            ? nonConditionalKeys
            : ((previousEntry?.[AppliedIncentivesNode]?.nonConditionals ?? []) as unknown[]).filter(isNonConditionalSelectionKey);
    const { allIds, conditionals, nonConditionals } = aggregatedSelectedIncentives(resolvedNonConditionalKeys, conditionalKeys);
    entry[AppliedIncentiveIdsNode] = allIds;
    entry[AppliedIncentivesNode] = { conditionals, nonConditionals };
};

export const handleIncentiveCases = (
    state: PenciledInputsState,
    action: any,
    offerType: OfferType,
    restorationEnabled = false,
    isCashFixesEnabled = false,
    ledgerEnabled = false
): PartialPenciledInputsWithAllTypes => {
    const keepFrozenProgramRows = ledgerEnabled;
    // Default to FINANCE if offerType is undefined
    const effectiveOfferType = offerType || FINANCE;

    switch (action.type) {
        case types.UPDATED_MANUAL_INCENTIVES: {
            const incomingManualIncentives = action.payload.manualIncentives;
            // `updateManualIncentives` builds the payload bag fresh from a row array, so it never carries
            // `ledgerOwned` - read ownership from state, or every caller of it silently strips the flag.
            const ledgerOwnedInState = Boolean(
                (state.byOfferType?.[effectiveOfferType]?.manualIncentives as { ledgerOwned?: boolean } | undefined)?.ledgerOwned
            );
            const resolvedManualIncentivesNode =
                restorationEnabled && incomingManualIncentives
                    ? {
                          ...withForwardManualIncentivesOnly(incomingManualIncentives, keepFrozenProgramRows),
                          ...(ledgerOwnedInState && { ledgerOwned: true })
                      }
                    : incomingManualIncentives;
            const manualIncentiveUpdates = updateInputWithValue(
                state.byOfferType?.[effectiveOfferType],
                'manualIncentives',
                resolvedManualIncentivesNode
            );

            const preserveSelection = restorationEnabled && Boolean(action.payload.preserveSelectedIncentives);
            manualIncentiveUpdates[SelectedIncentivesNode] = preserveSelection
                ? state.byOfferType?.[effectiveOfferType]?.[SelectedIncentivesNode] ?? []
                : [];

            const byOfferType = updateOfferTypeWithInputs(state.byOfferType, manualIncentiveUpdates, effectiveOfferType);
            return {
                ...state,
                byOfferType
            };
        }

        case types.MODIFY_MANUAL_INCENTIVE: {
            // Payment grid middleware re-dispatches this action for each cash-down value to trigger
            // additional payment calculations. Only the main payment should modify incentives state;
            // non-main grid dispatches are payment-only and must not add duplicate entries.
            if (action.meta?.paymentGrid?.gridAction && !action.meta?.paymentGrid?.isMainPayment) return state;

            const { incentive: incomingIncentive, action: modifyAction } = action.payload;
            const incentive =
                ledgerEnabled && isUserCustomIncentive(incomingIncentive) && isFrozenProgramRow(incomingIncentive)
                    ? { ...incomingIncentive, frozenFromProgram: undefined }
                    : incomingIncentive;
            const currentIncentives = state.byOfferType?.[effectiveOfferType]?.manualIncentives?.incentives || [];

            const matchesIncentiveByIdentity = (item: any, ignoreType: boolean) =>
                (incentive.code && item.description === incentive.description && String(item.code) === String(incentive.code)) ||
                (!incentive.code && incentive.name && item.name === incentive.name && (ignoreType || incentive.type === item.type));

            const matchesIncentive = (item: any, ignoreType = false) => {
                if (incentive._editId && item._editId) {
                    return item._editId === incentive._editId;
                }
                return matchesIncentiveByIdentity(item, ignoreType);
            };

            const findRowToEdit = () => {
                const byEditId = incentive._editId
                    ? currentIncentives.findIndex((item: any) => item._editId === incentive._editId)
                    : -1;
                if (byEditId !== -1) {
                    return byEditId;
                }
                if (isUserCustomIncentive(incentive) && !incentive.code) {
                    return -1;
                }
                return currentIncentives.findIndex((item) => matchesIncentiveByIdentity(item, restorationEnabled));
            };

            // Guards against a literal re-dispatch of the same 'add' (e.g. rapid field edits in ManualRebatesForm
            // each reading a stale isNewIncentive from an unrendered closure before the first dispatch's
            // isNewIncentive:false commits) appending a second, permanently-separate row: every later selector
            // pairs each raw row to itself perfectly by _editId, so nothing downstream can ever notice two rows
            // with the same _editId are duplicates. Only skips when the incoming row is identical in content to
            // an existing one sharing its _editId - a matching _editId with genuinely different content (e.g. a
            // manual-flag correction) still needs to reach dedupeManualIncentives below, which already resolves
            // that case correctly via its own _editId-first check.
            const isDuplicateAdd =
                isCashFixesEnabled &&
                effectiveOfferType === CASH &&
                Boolean(incentive._editId) &&
                currentIncentives.some(
                    (item: any) => item._editId === incentive._editId && JSON.stringify(item) === JSON.stringify(incentive)
                );
            const operations = {
                add: () => (isDuplicateAdd ? currentIncentives : [...currentIncentives, incentive]),
                remove: () => currentIncentives.filter((item) => !matchesIncentive(item)),
                edit: () => {
                    const existingIndex = restorationEnabled
                        ? findRowToEdit()
                        : currentIncentives.findIndex((item) => matchesIncentive(item, restorationEnabled));
                    if (existingIndex === -1)
                        return restorationEnabled && incentive.manual ? [...currentIncentives, incentive] : null;
                    // Editing a frozen row promotes it to a user row, so the next good PQ response leaves the
                    // operator's edit alone. Stripped here rather than trusting the form's payload.
                    const editedRow = isFrozenProgramRow(currentIncentives[existingIndex])
                        ? { ...incentive, frozenFromProgram: undefined, manual: true }
                        : incentive;
                    return [...currentIncentives.slice(0, existingIndex), editedRow, ...currentIncentives.slice(existingIndex + 1)];
                }
            };

            const updatedIncentives = operations[modifyAction as keyof typeof operations]?.();
            if (!updatedIncentives) return state;

            const resolvedIncentives = restorationEnabled
                ? dedupeManualIncentives(updatedIncentives).filter(shouldRetainManualRow(keepFrozenProgramRows))
                : updatedIncentives;

            const dealerCash = sumDealerCash(resolvedIncentives, restorationEnabled);
            const totalRebates = sumNonDealerCash(resolvedIncentives, restorationEnabled);

            // Rebuilds the bag rather than spreading it, so ownership has to be carried across explicitly.
            const wasLedgerOwned = Boolean(
                (state.byOfferType?.[effectiveOfferType]?.manualIncentives as { ledgerOwned?: boolean } | undefined)?.ledgerOwned
            );
            const manualIncentiveUpdates = updateInputWithValue(state.byOfferType?.[effectiveOfferType], ManualIncentivesNode, {
                incentives: resolvedIncentives,
                dealerCash,
                totalRebates,
                ...(wasLedgerOwned && { ledgerOwned: true })
            });

            manualIncentiveUpdates[SelectedIncentivesNode] = resolveSelectedIncentivesAfterManualChange(
                state,
                effectiveOfferType,
                restorationEnabled
            );

            const byOfferType = updateOfferTypeWithInputs(state.byOfferType, manualIncentiveUpdates, effectiveOfferType);
            return {
                ...state,
                byOfferType
            };
        }

        case types.LOCATION_STANDARDS_RECONCILED:
        case types.UPDATE_SELECTED_INCENTIVES: {
            const payload = action.payload;
            const programPayload =
                payload && typeof payload === 'object' && !Array.isArray(payload) && SelectedIncentivesNode in payload
                    ? payload
                    : { selectedIncentives: payload };
            const penciledInputs = applyPenciledInputsProgram(
                state,
                action,
                effectiveOfferType,
                programPayload,
                restorationEnabled || Boolean(action.meta?.preserveManualIncentives),
                restorationEnabled,
                Boolean(action.meta?.replaceNonConditionalSelection),
                keepFrozenProgramRows
            );
            return {
                ...state,
                byOfferType: penciledInputs
            };
        }

        case types.APPLIED_PROGRAM: {
            const payload = action.payload;
            const incentives = payload.incentives;
            const programData = payload.programData || {};
            const isManagerView = action.meta?.sourceApplication === ApplicationCodes.MV;
            const currentOfferType: PenciledInputOfferType = action.meta.currentOfferType;
            const mileage = programData.annualMiles;
            const appliedDownPayment = state.byOfferType?.[currentOfferType]?.downPayment;
            const term = selectors.getTermId(currentOfferType, programData.term, appliedDownPayment);
            const keepManualIncentives = restorationEnabled || action.meta?.keepManualIncentives || false;
            const adoptProgramRates = !action.meta?.reapplyCurrentProgram && !programRatesAreImplausible(programData);

            const existingManualIncentives = state.byOfferType?.[currentOfferType]?.manualIncentives;
            // No frozen rows retained on purpose: a newly applied program supersedes the previous one, and
            // the good PQ response it triggers re-freezes.
            const resolvedManualIncentives = keepManualIncentives
                ? keepUserManualIncentives(existingManualIncentives, restorationEnabled)
                : undefined;

            const newState: GenericObject = {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [currentOfferType]: {
                        ...state.byOfferType?.[currentOfferType as keyof typeof state.byOfferType],
                        manualIncentives: resolvedManualIncentives,
                        term
                    }
                }
            };

            if (currentOfferType === LEASE && mileage) {
                newState.byOfferType[currentOfferType].annualMiles = mileage;

                if (adoptProgramRates) {
                    applyLeaseProgramRates(newState.byOfferType[currentOfferType], programData, isManagerView);
                }

                newState.byOfferType[currentOfferType].acqFeeOverride = programData.acqFee;
                newState.byOfferType[currentOfferType].securityDepositOverride = programData.securityDeposit;
            }

            if (currentOfferType === FINANCE) {
                if (adoptProgramRates) {
                    applyFinanceProgramRates(newState.byOfferType[currentOfferType], programData, isManagerView);
                }
            }

            newState.byOfferType[currentOfferType].selectedIncentives = incentives;

            if (ledgerEnabled) {
                withAppliedSelectionNodes(
                    newState.byOfferType[currentOfferType],
                    (incentives ?? []) as string[],
                    state.byOfferType?.[currentOfferType as keyof typeof state.byOfferType] as GenericObject | undefined
                );
            }

            return newState as PartialPenciledInputsWithAllTypes;
        }

        default: {
            return state;
        }
    }
};
